import { useCallback, useMemo } from 'react'
import useDeepCompareEffect from 'use-deep-compare-effect'
import useTransactions from 'tennders-transactions/hooks/useTransactions'
import useSearchFilter from 'tennders-transactions/hooks/useSearchFilter'
import useSession from 'tennders-auth/hooks/useSession'
import useTransactionsStore, { transactionsInitialData } from 'tennders-market/bin/stores/useTransactionsStore'
import { SearchTypes } from 'tennders-common/types/SearchTypes'
import { dispatchDisableGrouping, setLoading } from 'tennders-common/stores/view/action'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { useDebounce } from 'use-debounce'
import forEach from 'lodash/forEach'
import { StateType } from 'tennders-common/types/StateType'

type useMyBoardProps = {
  refreshKey?: number | boolean
}

export const useMyBoard = ({ refreshKey = false }: useMyBoardProps = {}) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const { pathname, query } = router
  const { keyword, form, searchForm, setForm, reset } = useSearchFilter({ filterKey: SearchTypes.MY_BOARD })
  const { user } = useSession()
  const [isLoading] = useTransactionsStore((state: Record<string, any>) => [state.transactionsLoading])
  const page = useMemo(() => Number(query?.page) ?? form?.page ?? 1, [form, query?.page])
  const { fetchTransactions, transactions, pagination } = useTransactions()
  const tag = useSelector((state: StateType) => state.data.tag)

  const setPage = useCallback((page) => {
    setForm(prevState => ({ ...prevState, page }))
    router.push({
      pathname: '/',
      query: { ...query, page }
    })
  }, [query, searchForm])

  const [debouncedForm] = useDebounce(searchForm, 500)
  const [debouncedKeyword] = useDebounce(keyword, 500)

  const localDebouncedForm = useMemo(() => {
    const _f = debouncedForm
    if (refreshKey === SearchTypes.MY_BOARD && _f?.status === false) {
      _f.status = '-1'
    }
    return _f
  }, [debouncedForm, refreshKey])

  const onRefresh = useCallback(async () => {
    await fetchTransactions(localDebouncedForm)
  }, [localDebouncedForm])

  const search = (params = {}, mark = false) => {
    fetchTransactions({
      ...localDebouncedForm,
      ...(keyword && { query: keyword }),
      ...(tag && { transactionOrigin: tag }),
      ...params,
      history: false
    }, null, mark)
  }

  const onSorted = useCallback((sortby) => {
    dispatch(dispatchDisableGrouping())
    fetchTransactions(
      {
        ...localDebouncedForm,
        ...(keyword && { query: keyword }),
        ...(tag && { transactionOrigin: tag }),
        sortby: sortby.sortField || sortby.key,
        sortdirection: sortby.direction,
        history: false
      }
    )
  }, [localDebouncedForm, keyword, tag])

  useDeepCompareEffect(() => {
    const params = new URLSearchParams()
    forEach(Object.keys(localDebouncedForm), k => {
      const v = localDebouncedForm[k]
      if (v && v !== 'false') {
        if (typeof v === 'object') {
          params.append(k, JSON.stringify(v))
        } else if (Array.isArray(v)) {
          params.append(k, encodeURIComponent(JSON.stringify(v)))
        } else {
          params.append(k, v as string)
        }
      }
    })
    router.replace({ pathname: router.pathname, search: params.toString() })
  }, [debouncedKeyword, localDebouncedForm])

  useDeepCompareEffect(() => {
    if (user) {
      search({}, true)
    }
  }, [user, debouncedKeyword, localDebouncedForm, tag, page, pathname])

  const resetFilters = useCallback(() => {
    reset()
    router.push({ pathname: '/' })
  }, [reset])

  return {
    pagination: useMemo(() => ({ ...pagination, page, setPage }), [setPage, page, pagination]),
    onRefresh,
    resetForm: resetFilters,
    search,
    form,
    isLoading,
    items: transactions,
    transactions,
    onSorted,
    initialized: transactions !== transactionsInitialData
  }
}
